import { useCallback, useEffect, useState } from 'react';
import { checkSession } from '../components/hkuArts/api';
import AccessGate from '../components/hkuArts/AccessGate';
import KnowledgeModal from '../components/hkuArts/KnowledgeModal';
import { SidebarToggle } from '../components/hkuArts/Controls';
import StudentChat from '../components/hkuArts/StudentChat';
import ReferencePanel from '../components/hkuArts/ReferencePanel';
import '../components/hkuArts/demo.css';

const STORAGE_KEY = 'hku-arts-conversations-v1';
function newConversation() { return { id: `${Date.now()}-${Math.random()}`, title: 'New conversation', messages: [], context: {} }; }
function loadConversations() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (Array.isArray(stored) && stored.some((item) => typeof item?.id === 'string' && Array.isArray(item.messages))) return stored.filter((item) => typeof item?.id === 'string' && Array.isArray(item.messages)).map((item) => ({ ...item, messages: item.messages.map((message) => message.status === 'streaming' ? { ...message, status: 'stopped' } : message) }));
  } catch {}
  return [newConversation()];
}

export default function HkuArtsDemo() {
  const [phase, setPhase] = useState('checking');
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [conversations, setConversations] = useState(loadConversations);
  const [activeId, setActiveId] = useState(() => conversations[0]?.id);
  const [leftOpen, setLeftOpen] = useState(() => window.innerWidth > 760);
  const [rightOpen, setRightOpen] = useState(() => window.innerWidth > 1100);
  const [selection, setSelection] = useState({});
  const [renaming, setRenaming] = useState(null);
  const [storageError, setStorageError] = useState(false);

  useEffect(() => {
    let active = true;
    checkSession().then(() => { if (active) setPhase('ready'); }).catch(() => { if (active) setPhase('locked'); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations)); setStorageError(false); }
      catch { setStorageError(true); }
    }, 400);
    return () => clearTimeout(timer);
  }, [conversations]);
  const updateConversation = useCallback((id, data) => {
    setConversations((current) => current.map((item) => item.id === id ? {
      ...item, ...data, title: item.title === 'New conversation' && data.messages.length ? data.messages[0].content.slice(0, 48) : item.title
    } : item));
  }, []);
  function openConversation(id) { setActiveId(id); setSelection({}); if (window.innerWidth <= 760) setLeftOpen(false); }
  function addConversation() { const item = newConversation(); setConversations((current) => [item, ...current]); openConversation(item.id); }
  function deleteConversation(id) {
    const remaining = conversations.filter((item) => item.id !== id);
    if (!remaining.length) remaining.push(newConversation());
    setConversations(remaining);
    if (activeId === id) openConversation(remaining[0].id);
  }
  const active = conversations.find((item) => item.id === activeId);
  const selectedMessage = active?.messages.find((item) => item.id === selection.id) || [...(active?.messages || [])].reverse().find((item) => item.role === 'assistant');
  function showSources(id, number) { setSelection({ id, number }); setRightOpen(true); }

  if (phase === 'checking') return <div className="min-h-screen flex items-center justify-center text-ink-muted">Loading…</div>;
  if (phase === 'locked') return <AccessGate onGranted={() => setPhase('ready')} />;
  return <div className="arts-shell text-ink">
    {leftOpen && <>
      <button className="arts-mobile-scrim sidebar" aria-label="Close conversations" onClick={() => setLeftOpen(false)} />
      <aside className="arts-sidebar" aria-label="Conversations">
        <div className="flex items-center justify-between mb-8"><span className="font-semibold tracking-wide">EDVise <span className="text-ink-muted font-normal">/ Arts</span></span><SidebarToggle side="left" open onClick={() => setLeftOpen(false)} /></div>
        <button onClick={addConversation} className="arts-pill arts-new-chat mb-6">＋ New conversation</button>
        <div className="flex-1 overflow-auto space-y-1">{conversations.map((item) => <div key={item.id} className={`rounded-full px-3 py-3 ${item.id === activeId ? 'bg-surface' : ''}`}>
          {renaming === item.id ? <input autoFocus aria-label="Conversation name" defaultValue={item.title} maxLength={80} className="w-full bg-surface border border-edge p-1 text-sm" onBlur={(event) => { const title = event.target.value.trim(); if (title) setConversations((current) => current.map((row) => row.id === item.id ? { ...row, title } : row)); setRenaming(null); }} onKeyDown={(event) => { if (event.key === 'Enter') event.target.blur(); if (event.key === 'Escape') setRenaming(null); }} /> : <div className="flex items-center gap-1">
            <button onClick={() => openConversation(item.id)} className="truncate text-left flex-1 text-sm" title={item.title}>{item.messages.some((message) => message.status === 'streaming') ? '◌ ' : ''}{item.title}</button>
            <button onClick={() => setRenaming(item.id)} aria-label={`Rename ${item.title}`} className="text-xs text-ink-muted p-1">✎</button>
            <button onClick={() => deleteConversation(item.id)} aria-label={`Delete ${item.title}`} className="text-xs text-ink-muted p-1">×</button>
          </div>}
        </div>)}</div>
        <p className="text-xs text-ink-subtle pt-4" role={storageError ? 'status' : undefined}>{storageError ? 'Storage full. New changes are not saved.' : 'Saved on this device'}</p>
      </aside>
    </>}
    <main className="arts-main">
      <header className="arts-topbar">
        {!leftOpen && <SidebarToggle side="left" open={false} onClick={() => setLeftOpen(true)} />}
        <h1 className="text-sm font-medium flex-1 truncate">HKU Faculty of Arts</h1>
        {!rightOpen && <SidebarToggle side="right" open={false} onClick={() => setRightOpen(true)} />}
      </header>
      <div className="flex-1 min-h-0 relative">
        {conversations.map((item) => <div key={item.id} className="h-full" style={{ display: item.id === activeId ? 'block' : 'none' }}><StudentChat conversation={item} active={item.id === activeId} onChange={updateConversation} onSources={showSources} onOpenKnowledge={() => setKnowledgeOpen(true)} /></div>)}

      </div>
    </main>
    {rightOpen && <><button className="arts-mobile-scrim references" aria-label="Close references" onClick={() => setRightOpen(false)} /><ReferencePanel message={selectedMessage} number={selection.number} onClose={() => setRightOpen(false)} /></>}
    <KnowledgeModal open={knowledgeOpen} onClose={() => setKnowledgeOpen(false)} />
  </div>;
}
